import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { datePickerCellStyles } from '../../styles/DatePickerWidgetStyles';
import { PreFocusIndicator } from '../../styles/FocusIndicators';
import { EventUtilsOptOut } from '../../utils/ReactHelpers';

export interface DatePickerCellProps extends Pick<React.HTMLAttributes<HTMLElement>, 'aria-label' | 'onClick' | 'onFocus'> {
  isDisabled?: boolean;
  isFocused?: boolean;
  isInCurrMonth?: boolean;
  isSelected?: boolean;
  isTabbable?: boolean;
  isToday?: boolean;
}

const CellStyleName = TypeStyle.style(PreFocusIndicator, { $nest: { '&:focus': { position: 'relative' } } });

export class DatePickerCell extends React.PureComponent<DatePickerCellProps> {
  private element: HTMLElement;

  public componentDidMount(): void {
    this.syncFocus();
  }

  public componentDidUpdate(): void {
    this.syncFocus();
  }

  private handleRef = (element: HTMLDivElement) => {
    this.element = element;
  }

  private syncFocus(): void {
    if (this.element && this.props.isFocused) {
      this.element.focus();
    }
  }

  private getStyle(isDisabled: boolean, isInCurrMonth: boolean, isSelected: boolean, isToday: boolean): React.CSSProperties {
    let style;
    if (!isInCurrMonth) {
      style = datePickerCellStyles.inOtherMonths;
    } else if (isToday && isSelected) {
      style = datePickerCellStyles.todaySelected;
    } else if (isToday) {
      style = datePickerCellStyles.today;
    } else if (isSelected) {
      style = datePickerCellStyles.selected;
    } else {
      style = datePickerCellStyles.default;
    }
    if (isDisabled) {
      style = {
        ...style,
        ...datePickerCellStyles.disabled,
      };
    }
    return style;
  }

  public render(): JSX.Element {
    const { isInCurrMonth, isDisabled, isToday, isSelected, isTabbable } = this.props;
    const style = this.getStyle(!!isDisabled, !!isInCurrMonth, !!isSelected, !!isToday);
    // cells outside the current month should not be focusable when clicked
    const tabIndex = isInCurrMonth ? isTabbable ? 0 : -1 : undefined;
    const cellProps: React.HTMLAttributes<HTMLElement> = {
      ...EventUtilsOptOut,
      'aria-current': isToday || undefined,
      'aria-disabled': isDisabled || undefined,
      'aria-label': this.props['aria-label'],
      'aria-selected': isSelected || undefined,
      className: CellStyleName,
      onClick: this.props.onClick,
      onFocus: this.props.onFocus,
      role: 'gridcell',
      style,
      tabIndex,
    };

    return (
      <div {...cellProps} ref={this.handleRef}>
        {this.props.children}
      </div>
    );
  }
}
